<template>
  <FloatingMenu>
    <template #activator="{ show }">
      <ExecuteLockGuard>
        <button
          class="gap-1 px-4 py-2.5 text-sm rounded-lg flex items-center justify-center whitespace-nowrap bg-[#EEC239] text-[#816204] fill-yellow-800 w-full"
          :disabled="isDisabled"
          :class="{
            ' !bg-gray-100 !text-gray-400': isDisabled,
          }"
          @click="show"
        >
          Citation
          <ChevronDownIcon
            v-if="!isReviewReadOnly && arrow"
            class="size-4 fill-current"
          />
        </button>
      </ExecuteLockGuard>
    </template>
    <template #content="{ hide }">
      <div class="space-y-2 w-[500px] max-h-100">
        <RisDropZone v-model="citationFiles" />
        <button
          :disabled="citationFiles.length === 0"
          :class="{
            '!bg-black/5 !text-black/40': citationFiles.length === 0,
          }"
          class="w-full flex text-sm gap-1 items-center justify-center bg-blue-800 text-white rounded-lg px-4 py-3 font-medium text-center"
          @click="importCitation(citationFile, hide)"
        >
          <ImportIcon class="w-5" /> Import
        </button>
      </div>
    </template>
  </FloatingMenu>
</template>
<script setup lang="ts">
import FloatingMenu from '@app/components/Global/FloatingMenu.vue'
import { ChevronDownIcon } from '@heroicons/vue/16/solid'
import ImportIcon from '@app/components/Icons/ImportIcon.vue'
import useBuiltInImportSources from '@app/composables/use-built-in-import-sources'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { StudyState } from '@app/types'
import { injectStrict } from '@app/utils/injectStrict'
import RisDropZone from '@app/views/Review/Plan/Search/Components/RisDropZone.vue'
import { ReviewKey } from '@app/views/Review/use-review'
import { ReviewItem } from '@core/domain/models/reviewItem.model'
import { BuiltInImportSourceId } from '@core/domain/types/builtInImportSourceId'
import { computed, ref } from 'vue'
import ExecuteLockGuard from '../../ExecuteLockGuard.vue'

const props = withDefaults(
  defineProps<{
    reviewItem: ReviewItem
    isReviewReadOnly?: boolean
    arrow?: boolean
  }>(),
  {
    isReviewReadOnly: false,
    arrow: true,
  },
)
const builtInImportSources = useBuiltInImportSources()
const loading = useLoading()
const snackbar = useSnackbar()
const review = injectStrict(ReviewKey)
const citationFiles = ref<{ isValid: boolean; file: File }[]>([])
const citationFile = computed(() => citationFiles.value[0])
const citationSearch = builtInImportSources.findById(
  BuiltInImportSourceId.CITATION_SEARCH,
)!
const isDisabled = computed(() => {
  return (
    props.isReviewReadOnly || props.reviewItem.state !== StudyState.INCLUDED
  )
})
async function importCitation(
  file: { isValid: boolean; file: File },
  cb: () => void,
) {
  if (!file?.isValid) return
  loading.start()
  try {
    const dataSource = review.entity.value.plan?.importPlan.importSources?.find(
      (source) => source.id === BuiltInImportSourceId.CITATION_SEARCH,
    )
    if (!dataSource) {
      await review.addImportSourceToPlan({
        id: citationSearch.id,
        name: citationSearch.name,
        description: '',
        url: citationSearch.url ?? '',
        type: citationSearch.type,
      })
    }

    await review.importCitationSearch({
      citationFiles: [file.file],
      parentStudyId: props.reviewItem.id,
    })
    cb()
    snackbar.success('Success')
    citationFiles.value = []
  } catch (_e) {
    snackbar.error('Failed to import citation')
  } finally {
    loading.stop()
  }
}
</script>
